import React from 'react';
import {
  Card,
  Row,
  Col,
  Tag,
  Statistic,
  Descriptions,
  Table,
  Timeline,
  Tabs,
  Button,
  Breadcrumb,
  Space,
  Spin,
  Typography,
  Alert,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useOrderDetail } from '../../hooks/useOrders';
import { formatVND } from '@erpgold/shared';

const { Title } = Typography;

const statusColors: Record<string, string> = {
  pending: 'warning',
  confirmed: 'processing',
  completed: 'success',
  cancelled: 'error',
};

export const OrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { order, loading, error, refetch, cancelOrder } = useOrderDetail(id || '');
  const [cancelling, setCancelling] = React.useState(false);

  const handleCancelOrder = async () => {
    try {
      setCancelling(true);
      await cancelOrder('Cancelled by platform admin');
      message.success('Order cancelled successfully');
    } catch (err: unknown) {
      const maybe = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(maybe?.response?.data?.message || maybe?.message || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Lỗi tải dữ liệu"
        description={error.message || 'Vui lòng thử lại sau'}
        type="error"
        showIcon
        action={<Button onClick={refetch}>Thử lại</Button>}
      />
    );
  }

  if (!order) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Title level={4}>Order not found</Title>
        <Button onClick={() => navigate('/orders')}>Back to Orders</Button>
      </div>
    );
  }

  const itemColumns: ColumnsType<any> = [
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
    },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
      render: (value) => (value ? `${value}g` : '-'),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 140,
      align: 'right',
      render: (value) => formatVND(value),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
      align: 'center',
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 140,
      align: 'right',
      render: (value) => formatVND(value),
    },
  ];

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <Row gutter={24}>
          <Col span={14}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Customer">
                {order.customerName}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {order.customerPhone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {order.customerAddress || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Notes">
                {order.notes || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={10}>
            <Card title="Order Timeline" size="small">
              <Timeline
                items={
                  order.timeline?.map((item) => ({
                    color:
                      item.status === 'completed'
                        ? 'green'
                        : item.status === 'cancelled'
                        ? 'red'
                        : 'blue',
                    children: (
                      <div>
                        <strong>{item.status.toUpperCase()}</strong>
                        <br />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                          {dayjs(item.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                        </span>
                        {item.note && (
                          <>
                            <br />
                            <span style={{ fontSize: 12 }}>{item.note}</span>
                          </>
                        )}
                      </div>
                    ),
                  })) || []
                }
              />
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: 'items',
      label: 'Items',
      children: (
        <Table
          dataSource={order.items}
          columns={itemColumns}
          rowKey="id"
          pagination={false}
          size="small"
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5} align="right">
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <strong>{formatVND(order.total)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      ),
    },
    {
      key: 'payment',
      label: 'Payment',
      children: (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Payment Method">
            {order.paymentMethod}
          </Descriptions.Item>
          <Descriptions.Item label="Payment Status">
            <Tag color={order.paymentStatus === 'paid' ? 'success' : 'warning'}>
              {order.paymentStatus?.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Transaction ID">
            {order.transactionId || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Paid Amount">
            {order.paidAmount ? formatVND(order.paidAmount) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Change">
            {order.change ? formatVND(order.change) : '-'}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'tenant',
      label: 'Tenant',
      children: (
        <Card size="small">
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Tenant Name">
              <Link to={`/tenants/${order.tenantId}`}>{order.tenantName}</Link>
            </Descriptions.Item>
            <Descriptions.Item label="Tenant ID">
              {order.tenantId}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/orders">Orders</Link> },
          { title: `Order #${order.orderId}` },
        ]}
      />

      <Space style={{ marginBottom: 24 }} align="center">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/orders')}
        >
          Back
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          Order #{order.orderId}
        </Title>
        <Tag color={statusColors[order.status] || 'default'}>
          {order.status.toUpperCase()}
        </Tag>
        <span style={{ color: '#8c8c8c' }}>
          — {order.tenantName}
        </span>
        {(order.status === 'pending' || order.status === 'confirmed') && (
          <Button
            danger
            loading={cancelling}
            onClick={handleCancelOrder}
          >
            Cancel Order
          </Button>
        )}
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total"
              value={order.total}
              prefix={<DollarOutlined />}
              formatter={(value) => formatVND(value as number)}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Items"
              value={order.items?.length || 0}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Payment"
              value={order.paymentStatus || 'N/A'}
              prefix={<CheckCircleOutlined />}
              valueStyle={{
                color: order.paymentStatus === 'paid' ? '#52c41a' : '#faad14',
                fontSize: 20,
              }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Created"
              value={dayjs(order.createdAt).format('DD/MM/YYYY')}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs items={tabItems} />
      </Card>
    </div>
  );
};

export default OrderDetail;
